import { Camera } from 'domain/camera'
import { useAuth } from 'layout/context/auth-context'
import { useEffect, useRef, useState } from 'react'

interface StreamPlayerProps {
  camera: Camera
  onFullscreen?: () => void
}
const CameraPlayer: React.FC<StreamPlayerProps> = ({
  camera,
  onFullscreen,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const pcRef = useRef<RTCPeerConnection | null>(null)
  const [status, setStatus] = useState('Connecting...')
  const attemptsRef = useRef(0)
  const maxAttempts = 5
  const reconnectTimeoutRef = useRef<number>(0)
  const freezeCheckIntervalRef = useRef<number | null>(null)
  const lastTimeUpdateRef = useRef<number>(0)
  const { tokens } = useAuth()

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const baseApiUrl: string = process.env.REACT_APP_API_BASE_URL ?? ''
    const streamUrl = `${baseApiUrl}/stream/${camera.id}/whep`

    const startStream = async () => {
      // Check if we've exceeded max attempts
      if (attemptsRef.current >= maxAttempts) {
        setStatus(`Failed after ${maxAttempts} attempts`)
        console.error('[WebRTC] Max retry attempts reached')
        return
      }

      attemptsRef.current += 1
      const currentAttempt = attemptsRef.current

      if (currentAttempt > 1) {
        setStatus(`Reconnecting... (attempt ${currentAttempt}/${maxAttempts})`)
        console.log(`[WebRTC] Retry attempt ${currentAttempt}/${maxAttempts}`)
      }

      try {
        // Close existing connection if any
        if (pcRef.current) {
          pcRef.current.close()
        }

        const pc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        })
        pcRef.current = pc

        // Monitor ICE connection state for failures
        pc.oniceconnectionstatechange = () => {
          if (
            pc.iceConnectionState === 'failed' ||
            pc.iceConnectionState === 'disconnected'
          ) {
            console.log(
              `[WebRTC] Connection ${pc.iceConnectionState}, will retry...`,
            )
            // Retry after a delay
            const delay = Math.min(1000 * 2 ** (attemptsRef.current - 1), 16000)
            reconnectTimeoutRef.current = window.setTimeout(() => {
              startStream()
            }, delay) as unknown as number
          } else if (pc.iceConnectionState === 'connected') {
            // Reset attempts on successful connection
            attemptsRef.current = 0
            setStatus('Connected')
          }
        }

        pc.onconnectionstatechange = () => {
          if (pc.connectionState === 'failed') {
            console.error('[WebRTC] Connection failed')
            const delay = Math.min(1000 * 2 ** (attemptsRef.current - 1), 16000)
            reconnectTimeoutRef.current = window.setTimeout(() => {
              startStream()
            }, delay) as unknown as number
          }
        }

        pc.ontrack = (event) => {
          video.srcObject = event.streams[0]
          setStatus('Playing')

          // Start monitoring for frozen stream
          startFreezeDetection()
        }

        // Add transceivers
        pc.addTransceiver('video', { direction: 'recvonly' })
        pc.addTransceiver('audio', { direction: 'recvonly' })

        // Create and set local description
        const offer = await pc.createOffer()
        await pc.setLocalDescription(offer)

        // Wait for ICE gathering to complete
        await new Promise<void>((resolve) => {
          if (pc.iceGatheringState === 'complete') {
            resolve()
          } else {
            pc.onicegatheringstatechange = () => {
              if (pc.iceGatheringState === 'complete') {
                resolve()
              }
            }
          }
        })
        // Send offer to MediaMTX
        const response = await fetch(streamUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/sdp',
            Authorization: `Bearer ${tokens?.accessToken}`,
          },
          body: pc.localDescription!.sdp,
        })

        if (!response.ok) {
          throw new Error(`WHEP failed: ${response.status}`)
        }

        const answerSdp = await response.text()

        await pc.setRemoteDescription({
          type: 'answer',
          sdp: answerSdp,
        })
      } catch (error) {
        console.error('[WebRTC] Error:', error)
        setStatus(`Error: ${error}`)

        // Retry after delay with exponential backoff
        if (attemptsRef.current < maxAttempts) {
          const delay = Math.min(1000 * 2 ** (attemptsRef.current - 1), 16000)
          reconnectTimeoutRef.current = window.setTimeout(() => {
            startStream()
          }, delay) as unknown as number
        }
      }
    }

    // Freeze detection: monitors if video is actually playing
    const startFreezeDetection = () => {
      // Clear any existing interval
      if (freezeCheckIntervalRef.current) {
        clearInterval(freezeCheckIntervalRef.current)
      }

      // Track video time updates
      const handleTimeUpdate = () => {
        lastTimeUpdateRef.current = Date.now()
      }

      if (video) {
        video.addEventListener('timeupdate', handleTimeUpdate)
      }

      // Check every 5 seconds if the video is frozen
      freezeCheckIntervalRef.current = window.setInterval(() => {
        const timeSinceLastUpdate = Date.now() - lastTimeUpdateRef.current

        // If no time update for 7 seconds, video is likely frozen
        if (timeSinceLastUpdate > 7000 && status === 'Playing') {
          console.warn('[WebRTC] Stream appears frozen, reconnecting...')
          setStatus('Stream frozen, reconnecting...')

          // Clear freeze detection before restarting
          if (freezeCheckIntervalRef.current) {
            clearInterval(freezeCheckIntervalRef.current)
          }

          // Restart the stream
          startStream()
        }
      }, 5000) as unknown as number

      // Initialize the timestamp
      lastTimeUpdateRef.current = Date.now()
    }

    startStream()

    // Cleanup
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (freezeCheckIntervalRef.current) {
        clearInterval(freezeCheckIntervalRef.current)
      }
      if (pcRef.current) {
        pcRef.current.close()
        pcRef.current = null
      }
      if (video) {
        video.removeEventListener('timeupdate', () => {})
      }
      attemptsRef.current = 0
    }
  }, [camera.id, tokens])

  const getStatusBadge = () => {
    if (status === 'Playing') {
      return (
        <span className='camera-badge p-badge p-badge-success'>🔴 Ao Vivo</span>
      )
    } else if (status.includes('frozen')) {
      return (
        <span className='camera-badge p-badge p-badge-warning pulse'>
          ⚠️ Congelado
        </span>
      )
    } else if (status.includes('Reconnecting') || status === 'Connecting...') {
      return (
        <span className='camera-badge p-badge p-badge-warning pulse'>
          Conectando...
        </span>
      )
    } else if (status.includes('Connected')) {
      return (
        <span className='camera-badge p-badge p-badge-info'>Conectado</span>
      )
    } else {
      return (
        <span className='camera-badge p-badge p-badge-danger'>
          Desconectado
        </span>
      )
    }
  }

  const isConnecting =
    status === 'Connecting...' ||
    status.includes('Reconnecting') ||
    status.includes('frozen')
  const isError =
    status.includes('Failed') || (status.includes('Error') && !isConnecting)

  const getCameraTypeLabel = () => {
    return camera.model?.name ?? camera.model?.capabilities ?? ''
  }

  return (
    <div className='camera-card p-card'>
      <div className='camera-stream-header'>
        <div className='camera-title'>
          <i className='pi pi-video' />
          <span>{camera.name}</span>
        </div>
        {getStatusBadge()}
      </div>

      <div className='camera-info-bar'>
        <div className='camera-info-item'>
          <i className='pi pi-tag' />
          <span>{getCameraTypeLabel()}</span>
        </div>
        {camera.address && (
          <div className='camera-info-item'>
            <i className='pi pi-map-marker' />
            <span>{camera.address}</span>
          </div>
        )}
        {camera.latitude != null && camera.longitude != null && (
          <div className='camera-info-item'>
            <i className='pi pi-compass' />
            <span>
              {camera.latitude.toFixed(5)}, {camera.longitude.toFixed(5)}
            </span>
          </div>
        )}
        <div className='camera-info-item'>
          <i className='pi pi-globe' />
          <span>
            {camera.ipAddress}:{camera.httpPort}
          </span>
        </div>
      </div>

      <div className='camera-video-container'>
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className='camera-video'
        />
        {onFullscreen && (
          <button
            className='fullscreen-btn'
            onClick={onFullscreen}
            title='Tela cheia'
          >
            <i className='pi pi-window-maximize' />
          </button>
        )}
        {isConnecting && (
          <div className='camera-overlay'>
            <i
              className='pi pi-spin pi-spinner'
              style={{ fontSize: '3rem', color: '#3b82f6' }}
            />
            <p className='overlay-text'>{status}</p>
            <small>Aguarde enquanto estabelecemos a conexão...</small>
          </div>
        )}

        {isError && (
          <div className='camera-overlay error'>
            <i
              className='pi pi-exclamation-circle'
              style={{ fontSize: '3rem' }}
            />
            <p className='overlay-text'>Falha na Conexão</p>
            <small>{status}</small>
          </div>
        )}
      </div>
    </div>
  )
}

export default CameraPlayer
